var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { QueueType } from '../../multiskill/types';
import { AgentRequirementsTable } from '../agentRequirements';
import { PeriodSectionBody } from '../common/periodSectionBody';
import { PeriodSectionTable } from '../common/periodSectionTable';
import { getCountryExpansionKey, getLobExpansionKey, getMultiskillExpansionKeys, getMultiskillGroupExpansionKeys, getMultiskillSectionExpansionKey, getQueueExpansionKeysForLob, } from '../common/queueRow';
import { areAllOccupancyValues100Percent } from '../common/utils';
import { SupportStaffTable } from '../supportStaff';
export var shouldHideProductiveHoursSection = function (data) {
    var _a;
    if (!((_a = data.queues) === null || _a === void 0 ? void 0 : _a.length))
        return false;
    return data.queues.every(function (queue) {
        if (!queue.occupancy || !Array.isArray(queue.occupancy))
            return false;
        return areAllOccupancyValues100Percent(queue.occupancy);
    });
};
var getProdHoursRows = function (data) {
    var rows = ['occupancy', 'attrition', 'productionHours'];
    if (!shouldHideProductiveHoursSection(data)) {
        rows.splice(2, 0, 'productiveHours');
    }
    return rows;
};
var ProdHoursSectionBody = function (props) { return _jsx(PeriodSectionBody, __assign({}, props)); };
var getQueueExpansionItems = function (rows, lobs) {
    return rows.flatMap(function (item) {
        return lobs.flatMap(function (lob) { return __spreadArray([getLobExpansionKey(lob.lobName, item)], getQueueExpansionKeysForLob(lob, item), true); });
    });
};
var getNestedExpansionItems = function (rows, lobs, itemName) {
    if (rows.includes(itemName)) {
        return getQueueExpansionItems([itemName], lobs);
    }
    var rowItem = rows.find(function (item) { return itemName.startsWith("".concat(item, "::")); });
    if (!rowItem)
        return [];
    var lobEntry = lobs.find(function (lob) { return itemName === getLobExpansionKey(lob.lobName, rowItem); });
    if (!lobEntry)
        return [];
    return getQueueExpansionKeysForLob(lobEntry, rowItem);
};
export var ProdHoursPeriodTable = function (_a) {
    var _b, _c;
    var data = _a.data, index = _a.index, addColumns = _a.addColumns, scenarioSelector = _a.scenarioSelector;
    var t = useTranslation('report', { keyPrefix: 'period' }).t;
    var prodRows = React.useMemo(function () { return getProdHoursRows(data); }, [data]);
    var lobs = React.useMemo(function () { var _a; return (_a = data.queuesByLob) !== null && _a !== void 0 ? _a : []; }, [data.queuesByLob]);
    var isMultiskillPlan = data.dataType === QueueType.Multiskill;
    var seatsRequiredCountryOnly = isMultiskillPlan || (!((_b = data.queuesByLob) === null || _b === void 0 ? void 0 : _b.length) && !!((_c = data.countries) === null || _c === void 0 ? void 0 : _c.length));
    var workloadExpansionItems = React.useMemo(function () { return __spreadArray(__spreadArray(__spreadArray([], prodRows, true), getQueueExpansionItems(prodRows, lobs), true), prodRows.flatMap(function (row) { return getMultiskillExpansionKeys(data.queues, row); }), true); }, [data.queues, lobs, prodRows]);
    var seatsRequiredCountryExpansionItems = React.useMemo(function () {
        var _a, _b;
        return seatsRequiredCountryOnly
            ? (_b = (_a = data.countries) === null || _a === void 0 ? void 0 : _a.map(function (country) { return getCountryExpansionKey('seatsRequired', country.countryName); })) !== null && _b !== void 0 ? _b : []
            : [];
    }, [data.countries, seatsRequiredCountryOnly]);
    var seatsRequiredQueueExpansionItems = React.useMemo(function () { return (seatsRequiredCountryOnly ? [] : getQueueExpansionItems(['seatsRequired'], lobs)); }, [lobs, seatsRequiredCountryOnly]);
    var getWorkloadNestedExpansionItems = React.useCallback(function (itemName) {
        var multiskillRowItem = prodRows.find(function (row) { return itemName === getMultiskillSectionExpansionKey(row); });
        if (multiskillRowItem)
            return getMultiskillGroupExpansionKeys(data.queues, multiskillRowItem);
        return __spreadArray(__spreadArray([], getNestedExpansionItems(prodRows, lobs, itemName), true), (prodRows.includes(itemName)
            ? getMultiskillExpansionKeys(data.queues, itemName)
            : []), true);
    }, [data.queues, lobs, prodRows]);
    var seatsRequiredMultiskillExpansionItems = React.useMemo(function () { return getMultiskillExpansionKeys(data.queues, 'seatsRequired'); }, [data.queues]);
    var seatsRequiredMultiskillGroupExpansionItems = React.useMemo(function () { return getMultiskillGroupExpansionKeys(data.queues, 'seatsRequired'); }, [data.queues]);
    var getSeatsRequiredNestedExpansionItems = function (itemName) {
        if (itemName === 'seatsRequired') {
            return __spreadArray(__spreadArray(__spreadArray([], seatsRequiredQueueExpansionItems, true), seatsRequiredCountryExpansionItems, true), seatsRequiredMultiskillExpansionItems, true);
        }
        if (itemName === getMultiskillSectionExpansionKey('seatsRequired')) {
            return seatsRequiredMultiskillGroupExpansionItems;
        }
        return [];
    };
    return (_jsxs(_Fragment, { children: [_jsx(PeriodSectionTable, { title: t('workloadAndPerformance'), data: data, index: index, addColumns: addColumns, isProdHoursPlan: true, expansionItems: workloadExpansionItems, defaultExpanded: false, firstHeader: true, tableBodyComponent: function (props) { return _jsx(ProdHoursSectionBody, __assign({}, props, { rowItem: prodRows })); }, scenarioSelector: scenarioSelector, getNestedExpansionItems: getWorkloadNestedExpansionItems }), _jsx(AgentRequirementsTable, { data: data, index: index, addColumns: addColumns, isProdHoursPlan: true }), _jsx(PeriodSectionTable, { title: t('seatsRequired'), data: data, index: index, addColumns: addColumns, isProdHoursPlan: true, expansionItems: __spreadArray(__spreadArray(__spreadArray([
                    'seatsRequired'
                ], seatsRequiredCountryExpansionItems, true), seatsRequiredQueueExpansionItems, true), seatsRequiredMultiskillExpansionItems, true), getNestedExpansionItems: getSeatsRequiredNestedExpansionItems, defaultExpanded: false, tableBodyComponent: function (props) { return (_jsx(ProdHoursSectionBody, __assign({}, props, { rowItem: ['seatsRequired'], countryOnly: seatsRequiredCountryOnly }))); } }), _jsx(SupportStaffTable, { data: data, index: index, addColumns: addColumns, isProdHoursPlan: true })] }));
};
